import { getCurrencyCode } from '@/helpers';
import { OrderWithContext, SubmitOrderRequestPaymentMethod, SubmitOrderRequestPaymentMethodType } from '@/models';
import { CountryCode } from '@/store/ui';
import Vue from 'vue';
import { Component } from 'vue-property-decorator';

const baseRequest = {
  apiVersion: 2,
  apiVersionMinor: 0,
  allowedPaymentMethods: [] as google.payments.api.PaymentMethodSpecification[],
};

const allowedCardNetworks: google.payments.api.CardNetwork[] = ['AMEX', 'DISCOVER', 'MASTERCARD', 'VISA'];
const allowedCardAuthMethods: google.payments.api.CardAuthMethod[] = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];
let paymentsClient: google.payments.api.PaymentsClient | null = null;

function getGooglePaymentsClient(googlePayEnvironment: google.payments.api.Environment, googlePayMerchantId: string): google.payments.api.PaymentsClient {
  if (paymentsClient === null) {
    paymentsClient = new google.payments.api.PaymentsClient({
      environment: googlePayEnvironment,
      merchantInfo: {
        merchantName: 'CMG Strategy Co., LLC',
        merchantId: googlePayMerchantId
      },
    });
  }
  return paymentsClient;
}

@Component({
  name: 'CmgGooglePayButton',
})
export default class CmgGooglePayButton extends Vue {
  tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification = {
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: this.$services.config.getGooglePayMerchantGateway(),
      gatewayMerchantId: this.$services.config.getGooglePayMerchantGatewayId(),
    }
  };

  cardPaymentMethod: google.payments.api.PaymentMethodSpecification = {
    type: 'CARD',
    parameters: {
      allowedAuthMethods: allowedCardAuthMethods as google.payments.api.CardAuthMethod[],
      allowedCardNetworks: allowedCardNetworks as google.payments.api.CardNetwork[],
      billingAddressRequired: true,
        billingAddressParameters: {
          format: 'MIN',
        }
    },
    tokenizationSpecification: this.tokenizationSpecification
  };

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get currencyCode(): string {
    return getCurrencyCode(this.countryCode);
  }

  get currentOrder(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get selectedPaymentMethods(): SubmitOrderRequestPaymentMethod[] {
    return this.$appStore.state.checkout.payment.methods;
  }

  get selectedGiftCardPayment(): SubmitOrderRequestPaymentMethod | undefined {
    return this.selectedPaymentMethods.find(payment => payment.giftCardNumber);
  }

  get giftCardBalance(): number {
    const selectedGiftCardNumber = this.selectedGiftCardPayment?.giftCardNumber;
    if (!selectedGiftCardNumber) {
      return 0;
    }

    const wallet = this.$appStore.state.customer.wallet;
    return wallet.giftCardBalances[selectedGiftCardNumber] || 0;
  }

  get partialPaymentAmount(): number {
    const orderTotalAmount = this.currentOrder?.order.orderTotalAmount || 0;
    if (!this.selectedGiftCardPayment || this.giftCardBalance <= 0 || this.giftCardBalance >= orderTotalAmount) {
      return 0;
    }

    return orderTotalAmount - this.giftCardBalance;
  }

  async mounted() {
    paymentsClient = getGooglePaymentsClient(this.$services.config.getGooglePayEnvironment(), this.$services.config.getGooglePayMerchantId());
    const isReadyToPayRequest = Object.assign({}, baseRequest);
    isReadyToPayRequest.allowedPaymentMethods = [this.cardPaymentMethod];

    try {
      const response = await paymentsClient?.isReadyToPay(isReadyToPayRequest);
      if (response?.result) {
        const button = paymentsClient?.createButton({
          buttonSizeMode: 'fill',
          onClick: () => {
            this.onGooglePaymentButtonClicked();
          }
        });
        if (button) {
          document?.getElementById('google-pay-button-container')?.appendChild(button);
        }
      }
    } catch (error: any) {
      this.$services.logger.error('Error initializing Google Pay Button:', error);
    }
  }

  async onGooglePaymentButtonClicked(): Promise<void> {
    if (!paymentsClient) {
      paymentsClient = getGooglePaymentsClient(this.$services.config.getGooglePayEnvironment(), this.$services.config.getGooglePayMerchantId());
    }
    const paymentDataRequest = this.getGooglePaymentDataRequest();

    let paymentData: google.payments.api.PaymentData | undefined;

    try {
      paymentData = await paymentsClient?.loadPaymentData(paymentDataRequest);
    } catch (error: any) {
      this.$services.logger.error('Error loading payment data:', error);
      return;
    }

    try {   
      if (paymentData) {
        await this.processPayment(paymentData);
      }
    } catch (error: any) {
      this.$services.logger.error('Error processing payment:', error);
    }
  }

  getGooglePaymentDataRequest(): google.payments.api.PaymentDataRequest {
    const paymentDataRequest = Object.assign({}, baseRequest) as google.payments.api.PaymentDataRequest;
    paymentDataRequest.allowedPaymentMethods = [this.cardPaymentMethod];
    paymentDataRequest.transactionInfo = this.getGoogleTransactionInfo();
    paymentDataRequest.merchantInfo = {
      merchantId: this.$services.config.getGooglePayMerchantId(),
      merchantName: 'CMG Strategy Co., LLC'
    };

    return paymentDataRequest;
  }

  getGoogleTransactionInfo(): google.payments.api.TransactionInfo {
    const totalPrice = this.partialPaymentAmount > 0
      ? this.partialPaymentAmount.toFixed(2)
      : this.currentOrder?.order.orderTotalAmount.toFixed(2) || '0.00';

    return {
      countryCode: this.countryCode,
      currencyCode: this.currencyCode,
      totalPriceStatus: 'FINAL',
      totalPrice,
      totalPriceLabel: 'Total'
    };
  }

  async processPayment(paymentData: google.payments.api.PaymentData): Promise<void> {
    const paymentMethodsToBeSubmitted: SubmitOrderRequestPaymentMethod[] = [];

    if (this.selectedGiftCardPayment && this.partialPaymentAmount > 0) {
      paymentMethodsToBeSubmitted.push({ ...this.selectedGiftCardPayment });
    }

    const token = paymentData.paymentMethodData.tokenizationData.token;
    const tokenPayload = this.tryParseJson(token);
    const signedMessage = this.tryGetSignedMessage(tokenPayload);

    paymentMethodsToBeSubmitted.push({
      paymentType: SubmitOrderRequestPaymentMethodType.GooglePay,
      amount: this.partialPaymentAmount > 0
        ? parseFloat(this.partialPaymentAmount.toFixed(2))
        : this.currentOrder?.order.orderTotalAmount,
      paymentProviderId: this.$appStore.state.checkout.paymentProvider,
      merchantIdentifier: 'Chipotle',
      nativeWalletData: this.base64EncodeUtf8(tokenPayload.signedMessage),
      paySignature: tokenPayload?.signature,
      ephemeralPublicKey: signedMessage?.ephemeralPublicKey,
      tag: signedMessage?.tag,
      creditCardZipCode: paymentData.paymentMethodData.info?.billingAddress?.postalCode
    });

    this.$services.checkout.setPaymentMethods(paymentMethodsToBeSubmitted);

    const checkoutPage = this.$parent as { onSubmitOrder?: () => Promise<void> } | null;
    if (!checkoutPage || typeof checkoutPage.onSubmitOrder !== 'function') {
      throw new Error('Unable to submit order from Google Pay button.');
    }
    await checkoutPage.onSubmitOrder();
  }

  private tryParseJson(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private tryGetSignedMessage(tokenPayload: any): { encryptedMessage?: string; ephemeralPublicKey?: string; tag?: string } | null {
    if (!tokenPayload || typeof tokenPayload.signedMessage !== 'string') {
      return null;
    }

    return this.tryParseJson(tokenPayload.signedMessage);
  }

  base64EncodeUtf8(str: string) {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}